import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { getEnvironmentName } from '@squareup/dex-utils-environment';
import React, { FunctionComponent } from 'react';

import { DynamicSvelteComponent } from '../SvelteComponent';

interface FeedbackWidgetProps {
  componentId: number;
  subcomponentId: string;
  floating: boolean;
  showPrompt?: boolean;
  onComponentSubmitted?: (e: CustomEvent) => void;
}

function getSvelteComponentProps(props: FeedbackWidgetProps) {
  return {
    componentId: props.componentId,
    subcomponentId: props.subcomponentId,
    environment: getEnvironmentName(),
    url: props.subcomponentId,
    floating: props.floating,
  };
}

function getReactProps(props: FeedbackWidgetProps) {
  return {
    onComponentSubmitted: props.onComponentSubmitted,
    showPrompt: props.showPrompt ?? false,
  };
}

const ClientSideComponent = import('@squareup/svelte-dex-feedback').then(
  (mod) => {
    return mod.FeedbackWidget;
  }
);

export const FeedbackWidget: FunctionComponent<
  FeedbackWidgetProps & NullableClassName
> = React.memo(
  ({ className, ...rest }) => {
    const props = getSvelteComponentProps(rest);
    const reactProps = getReactProps(rest);
    const Children = () => (
      <DynamicSvelteComponent
        clientSideComponent={ClientSideComponent}
        componentProps={props}
        reactProps={reactProps}
      ></DynamicSvelteComponent>
    );

    // Svelte components do not accept classnames so for now add a div when needed.
    return className ? (
      <Box className={className}>
        <Children />
      </Box>
    ) : (
      <Children />
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.componentId === nextProps.componentId &&
      prevProps.subcomponentId === nextProps.subcomponentId &&
      prevProps.floating === nextProps.floating &&
      prevProps.showPrompt === nextProps.showPrompt &&
      // If FeedbackWidget is showing the prompt (showPrompot == true), it should be re-rendered.
      // Otherwiser, it should not be re-rendered to reduce widget flickering.
      nextProps.showPrompt === false
    );
  }
);

FeedbackWidget.displayName = 'FeedbackWidget';
